import {
  Card,
  CardResponse,
  CardResponseMeta,
  Pointer,
  Notification,
} from '@yleisradio/areena-types';
import useSWR, { KeyedMutator } from 'swr';
import { getCards } from 'services/areena-api/fetchers';
import { useLocationParameters } from './useLocationParameters';
import logger from 'services/logger';
import { useTunnusContext } from 'contexts/TunnusContext';

interface HookReturn {
  readonly cards: Card[];
  readonly errored: boolean;
  readonly loading: boolean;
  readonly mutate: KeyedMutator<CardResponse>;
  readonly notifications: Notification[] | undefined;
  readonly totalCount: number;
  readonly meta: CardResponseMeta | undefined;
}

interface Params {
  source: Pointer | undefined;
  pageIndex: number;
  pageSize: number;
  filterParameters?: Record<string, string>;
  refreshInterval?: number;
}

type FetcherParameters = [
  source: Pointer,
  offset: number,
  limit: number,
  filterParameters: Record<string, string>,
  locationParameters: Record<string, string>,
  isUserAuthenticated: boolean,
  // key-only: scopes personalized lists per user so cache never leaks across users
  userId: string | undefined,
];

const fetcher = ([
  source,
  offset,
  limit,
  filterParameters,
  locationParameters,
  isUserAuthenticated,
]: FetcherParameters): Promise<CardResponse> =>
  getCards(
    source,
    offset,
    limit,
    isUserAuthenticated,
    locationParameters,
    filterParameters
  );

function isPersonalList(source: Pointer | undefined): boolean {
  if (!source?.uri) {
    return false;
  }

  try {
    return new URL(source.uri).pathname.startsWith('/v1/ui/profile/');
  } catch {
    return false;
  }
}

export function useCards({
  source,
  pageIndex,
  pageSize,
  filterParameters = {},
  refreshInterval = 0,
}: Params): HookReturn {
  const locationParameters = useLocationParameters();
  const { isLoggedIn: isUserAuthenticated, user } = useTunnusContext();
  const isLiveData = refreshInterval > 0;

  const offset = pageIndex * pageSize;
  const limit = pageSize;
  const fetcherParams: FetcherParameters | null =
    source && locationParameters
      ? [
          source,
          offset,
          limit,
          filterParameters,
          locationParameters,
          isUserAuthenticated ?? false,
          user?.userId,
        ]
      : null;

  const { data, error, isLoading, mutate } = useSWR(fetcherParams, fetcher, {
    refreshInterval,
    revalidateOnFocus: isLiveData,
    revalidateIfStale: isLiveData || isPersonalList(source),
    // retry authenticated (personalized) fetches so a transient error during a
    // login/user switch recovers instead of leaving the list stuck empty
    shouldRetryOnError: isUserAuthenticated,
    errorRetryCount: 3,
  });

  if (error) {
    logger.error(
      error,
      `Error occurred while fetching ${limit} cards starting from index ${offset} for list "${source?.uri}".`
    );
  }

  return {
    cards: data?.data || [],
    errored: !!error,
    loading: !fetcherParams || isLoading,
    mutate,
    totalCount: data?.meta?.count || 0,
    notifications: data?.notifications,
    meta: data?.meta,
  };
}
